import React, { useEffect, useState } from "react";
import { Amplify, Auth } from "aws-amplify";
import { USER_AUTH_CONFIG } from "../aws-config";
import { PAMPAS_CAT } from "../config";
import {
  Button,
  Typography,
  Stack,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  TextField,
  Card,
  IconButton,
} from "@mhome/ui";
import { useRecoilState } from "recoil";
import { platformState } from "../global-state";
import { useAppInterface } from "../app/app-interface-provider";
import { useHistory } from "react-router-dom";
import { useMediaQuery } from "@mui/material";
import { Visibility, VisibilityOff, OpenInNew } from "@mui/icons-material";
import { checkEmailExists } from "../service/user-login-service";
import useApiCall from "../common/api-call";

const UserLogin = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [password, setPassword] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [passwordError, setPasswordError] = useState(false);
  const [email, setEmail] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showResetFields, setShowResetFields] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const history = useHistory();
  const isMobile = useMediaQuery("@media (max-width:959px)");
  const { apiCall } = useApiCall();
  const [countdown, setCountdown] = useState(0);
  const [isCountdownActive, setIsCountdownActive] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [platform, setPlatform] = useRecoilState(platformState);
  const appInterface = useAppInterface();
  const [showPassword, setShowPassword] = useState(false);

  const [validations, setValidations] = useState({
    hasLowerCase: false,
    hasUpperCase: false,
    hasNumber: false,
    hasSpecialChar: false,
    hasMinLength: false,
    noLeadingTrailingSpace: false,
  });

  useEffect(() => {
    Amplify.configure(USER_AUTH_CONFIG);
  }, []);

  const validatePassword = (pwd) => {
    setValidations({
      hasLowerCase: /[a-z]/.test(pwd),
      hasUpperCase: /[A-Z]/.test(pwd),
      hasNumber: /\d/.test(pwd),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(pwd),
      hasMinLength: pwd.length >= 8,
      noLeadingTrailingSpace: pwd.trim().length === pwd.length,
    });
  };

  const handlePasswordChange = (e) => {
    const value = e.target.value;
    setPassword(value);
    validatePassword(value);
  };

  const allValid = Object.values(validations).every(Boolean);

  const startCountdown = () => {
    setCountdown(60);
    setIsCountdownActive(true);

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev === 1) {
          clearInterval(timer);
          setIsCountdownActive(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSignIn = async () => {
    setErrorMessage(null);

    // Check if terms are accepted
    if (!termsAccepted) {
      setErrorMessage(
        "Please accept the Terms of Use and Privacy Policy to continue."
      );
      return;
    }

    try {
      if (!email) {
        setEmailError(true);
      } else {
        setEmailError(false);
      }

      if (!password) {
        setPasswordError(true);
      } else {
        setPasswordError(false);
      }

      if (!email || !password) {
        return;
      }

      await Auth.signIn(email, password);
      localStorage.removeItem("filterParams-taskflow");
      localStorage.removeItem("filterParams-recurring");
      window.location.href = "/";
    } catch (error) {
      setErrorMessage("Failed to sign in. Invalid username or password.");
    }
  };

  const handleForgotPassword = async () => {
    try {
      if (!email) {
        setErrorMessage("Please enter the email.");
        return;
      }
      await Auth.forgotPassword(email);
      setShowResetFields(true);
      setNewPassword("");
      setResetCode("");
      setInfoMessage("Password reset code sent to your email.");
    } catch (error) {
      setErrorMessage("Failed to send reset code. Please try again.");
    }
  };

  const handleResetPassword = async () => {
    try {
      await Auth.forgotPasswordSubmit(email, resetCode, newPassword);
      setInfoMessage("Password reset successful! You can now sign in.");
      setShowResetFields(false);
      setActiveTab(0); // Switch to SignIn tab after reset
    } catch (error) {
      setErrorMessage("Failed to reset password. Please check your code.");
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      // Handle Google sign-in in web browser
      await Auth.federatedSignIn({ provider: "Google" });
    } catch (error) {
      console.error("Google sign-in error:", error);
      setErrorMessage("Failed to sign in with Google. Please try again.");
    }
  };

  const handleSignUp = async () => {
    setErrorMessage("");
    setInfoMessage("");

    // Check if terms are accepted
    if (!termsAccepted) {
      setErrorMessage(
        "Please accept the Terms of Use and Privacy Policy to continue."
      );
      return;
    }

    try {
      const valid = await checkEmailPassword();
      if (!valid) {
        return;
      }

      console.log("Starting manual registration for email:", email);
      await Auth.signUp({
        username: email,
        password,
        attributes: { email },
      });
      console.log("Sign up successful, verification code sent.");
      setInfoMessage(
        "Verification code sent to your email.Valid within five minutes"
      );
      setShowConfirm(true);
      setCodeSent(true);
      startCountdown();
    } catch (error) {
      console.error("Error during sign up:", error);
      setErrorMessage("Error signing up. Please try again.");
    }
  };

  const checkEmailPassword = async () => {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const validEmail = emailPattern.test(email);

    if (!email || !validEmail) {
      setErrorMessage("Invalid email");
      setEmailError(true);
      return false;
    } else {
      setEmailError(false);
    }

    const criteria = {
      lowerCase: /[a-z]/, // At least one lowercase letter
      upperCase: /[A-Z]/, // At least one uppercase letter
      number: /[0-9]/, // At least one number
      specialCharacter: /[!@#$%^&*(),.?":{}|<>]/, // At least one special character
      length: /^.{8,}$/, // At least 8 characters
      noLeadingTrailingSpace: /^\S.*\S$|^\S$/, // No leading or trailing spaces
    };

    const validPwd =
      criteria.lowerCase.test(password) &&
      criteria.upperCase.test(password) &&
      criteria.number.test(password) &&
      criteria.specialCharacter.test(password) &&
      criteria.length.test(password) &&
      criteria.noLeadingTrailingSpace.test(password);

    if (!password || !validPwd) {
      setErrorMessage("Invalid password");
      setPasswordError(true);
      return false;
    } else {
      setPasswordError(false);
    }

    const emailExists = await checkEmailExists(apiCall, email);

    if (emailExists) {
      setErrorMessage("This email is already registered. Please log in.");
      return false;
    }

    return true;
  };

  const handleResendCode = async () => {
    // Check if terms are accepted
    if (!termsAccepted) {
      setErrorMessage(
        "Please accept the Terms of Use and Privacy Policy to continue."
      );
      return;
    }

    const valid = await checkEmailPassword();
    if (!valid) {
      return;
    }

    try {
      await Auth.resendSignUp(email);
      console.log("Verification code resent successfully");
      setInfoMessage(
        "New verification code sent to your email. Valid within five minutes"
      );
      setShowConfirm(true);
      startCountdown();
    } catch (error) {
      console.error(error);
      setErrorMessage(error.message);
    }
  };

  const handleNewPasswordChange = (e) => {
    const value = e.target.value;
    setNewPassword(value);
    validatePassword(value);
  };

  const handleConfirmSignUp = async () => {
    // Check if terms are accepted
    if (!termsAccepted) {
      setErrorMessage(
        "Please accept the Terms of Use and Privacy Policy to continue."
      );
      return;
    }

    try {
      await Auth.confirmSignUp(email, verificationCode);
      setInfoMessage("Sign up successful! You can now sign in.");
      setShowConfirm(false);
      handleTabSwitch(0);
    } catch (error) {
      setErrorMessage("Invalid verification code. Please try again.");
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleTabSwitch = (tab) => {
    setErrorMessage("");
    setInfoMessage("");
    setActiveTab(tab);
  };

  const handleAuthorizeInBrowser = async () => {
    if (platform.source === "desktop") {
      const url = `${PAMPAS_CAT}?source=desktop`;
      appInterface.openAuthUrl(url);
      console.log("Navigating to desktop auth wait");
      history.push("/desktop-auth-wait");
    } else if (platform.source === "app") {
      const url = `${PAMPAS_CAT}?source=app`;
      // Call JS interface to let Android/iOS open the web
      if (appInterface.isAvailable()) {
        await appInterface.openAuthUrl(url);
      }
      console.log("Requesting mobile app to open browser");
    }
  };

  return (
    <div
      className="flex justify-center items-center"
      style={{
        minHeight: "100vh",
        background: isMobile
          ? "hsl(var(--background))"
          : "hsl(var(--background))",
      }}
    >
      <Card
        elevation={0}
        style={{
          width: isMobile ? "100%" : window.innerWidth >= 960 ? "500px" : "90%",
          height: "auto",
          display: "flex",
          flexDirection: "column",
          boxShadow: 0,
          borderRadius: "12px",
          backgroundColor: isMobile ? "transparent" : "hsl(var(--background))",
        }}
      >
        <div
          className="flex flex-col justify-center"
          style={{
            height: "100%",
            padding: "32px",
            backgroundColor: isMobile
              ? "transparent"
              : "hsl(var(--background))",
            overflowY: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
        >
          <style>
            {`
              div::-webkit-scrollbar {
                display: none;
              }
            `}
          </style>
          {/* Company Branding */}
          <div
            className="flex items-center justify-center"
            style={{
              marginBottom: "24px",
            }}
          >
            <img
              src="/logo512.png"
              alt="mHome AI Logo"
              style={{
                width: 96,
                height: 96,
                marginRight: "8px",
              }}
            />
            <div className="flex flex-col justify-center">
              <Typography
                variant="h4"
                component="div"
                style={{
                  fontWeight: 600,
                  fontFamily: "Inter",
                  letterSpacing: "-0.01em",
                  color: "#475B63",
                  lineHeight: 1.2,
                  textTransform: "uppercase",
                }}
              >
                mHome
              </Typography>
              <Typography
                variant="body1"
                component="div"
                style={{
                  fontWeight: 400,
                  fontFamily: "Inter",
                  color: "#475B63",
                  marginTop: "4px",
                }}
              >
                Orchestrate Your Entire Home
              </Typography>
            </div>
          </div>

          {errorMessage && (
            <Typography color="error" align="center">
              {errorMessage}
            </Typography>
          )}
          {infoMessage && (
            <Typography color="primary" align="center">
              {infoMessage}
            </Typography>
          )}

          {platform.source === "desktop" || platform.source === "app" ? (
            <>
              <Typography
                variant="h5"
                align="center"
                style={{
                  marginTop: "16px",
                  marginBottom: "8px",
                  color: "hsl(var(--muted-foreground))",
                  animation: "pulse 1.5s ease-in-out infinite",
                }}
              >
                <style>
                  {`
                    @keyframes pulse {
                      0% { opacity: 0.6; }
                      50% { opacity: 1; }
                      100% { opacity: 0.6; }
                    }
                  `}
                </style>
                Directing to sign-in page...
              </Typography>
              <Button
                variant="outlined"
                onClick={handleAuthorizeInBrowser}
                fullWidth
                style={{
                  marginTop: "24px",
                  marginBottom: "8px",
                  borderRadius: "50px",
                  textTransform: "none",
                  borderWidth: 2,
                  borderColor: "hsl(var(--primary))",
                  color: "hsl(var(--primary))",
                  fontWeight: "bold",
                  position: "relative",
                  boxShadow: "none",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderWidth = "2px";
                  e.currentTarget.style.backgroundColor =
                    "rgba(0, 150, 136, 0.08)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderWidth = "2px";
                  e.currentTarget.style.backgroundColor = "transparent";
                }}
                startIcon={<OpenInNew />}
              >
                Open Sign-in Page
              </Button>
            </>
          ) : (
            <>
              <div style={{ marginTop: "16px" }}>
                {!showResetFields && (
                  <>
                    <TextField
                      label="Email"
                      variant="outlined"
                      fullWidth
                      size="medium"
                      floatingLabel={true}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onFocus={() => setEmailError(false)}
                      error={emailError}
                      style={{
                        borderRadius: "16px",
                        height: "56px",
                      }}
                      InputProps={{
                        style: {
                          height: "56px",
                        },
                      }}
                    />
                    {activeTab == 0 && (
                      <div style={{ marginTop: "16px" }}>
                        <TextField
                          label="Password"
                          variant="outlined"
                          type={showPassword ? "text" : "password"}
                          fullWidth
                          size="medium"
                          floatingLabel={true}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          onFocus={() => setPasswordError(false)}
                          error={passwordError}
                          style={{
                            borderRadius: "16px",
                            height: "56px",
                          }}
                          InputProps={{
                            style: {
                              height: "56px",
                            },
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={togglePasswordVisibility}
                                  onMouseDown={(e) => e.preventDefault()} // Prevent focus loss on mouse down
                                >
                                  {showPassword ? (
                                    <VisibilityOff />
                                  ) : (
                                    <Visibility />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </div>
                    )}
                  </>
                )}

                {activeTab == 1 && (
                  <>
                    <TextField
                      label="Password"
                      variant="outlined"
                      type={showPassword ? "text" : "password"}
                      fullWidth
                      size="medium"
                      floatingLabel={true}
                      value={password}
                      onChange={(e) => handlePasswordChange(e)}
                      onFocus={() => setPasswordError(false)}
                      error={passwordError}
                      style={{
                        marginTop: "16px",
                        borderRadius: "16px",
                        height: "56px",
                      }}
                      InputProps={{
                        style: {
                          height: "56px",
                        },
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={togglePasswordVisibility}
                              onMouseDown={(e) => e.preventDefault()} // Prevent focus loss on mouse down
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {password && (
                      <div
                        className="grid grid-cols-2 gap-1"
                        style={{
                          marginTop: "8px",
                          fontSize: "0.75rem",
                        }}
                      >
                        <li
                          style={{
                            color: validations.hasLowerCase ? "green" : "red",
                          }}
                        >
                          {validations.hasLowerCase ? "✓" : "×"} Lowercase
                          letter
                        </li>
                        <li
                          style={{
                            color: validations.hasUpperCase ? "green" : "red",
                          }}
                        >
                          {validations.hasUpperCase ? "✓" : "×"} Uppercase
                          letter
                        </li>
                        <li
                          style={{
                            color: validations.hasNumber ? "green" : "red",
                          }}
                        >
                          {validations.hasNumber ? "✓" : "×"} Number
                        </li>
                        <li
                          style={{
                            color: validations.hasMinLength ? "green" : "red",
                          }}
                        >
                          {validations.hasMinLength ? "✓" : "×"} Min 8 chars
                        </li>
                        <li
                          style={{
                            color: validations.hasSpecialChar ? "green" : "red",
                          }}
                        >
                          {validations.hasSpecialChar ? "✓" : "×"} Special char
                        </li>
                        {!validations.hasSpecialChar && (
                          <li
                            style={{
                              color: "red",
                              gridColumn: "span 2",
                              listStyle: "none",
                              fontSize: "0.7rem",
                              marginLeft: "-1em",
                            }}
                          >
                            Accepted: !@#$%^&*(),.?&quot;:{}|&lt;&gt;
                          </li>
                        )}
                        <li
                          style={{
                            color: validations.noLeadingTrailingSpace
                              ? "green"
                              : "red",
                          }}
                        >
                          {validations.noLeadingTrailingSpace ? "✓" : "×"} No
                          leading/trailing space
                        </li>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div style={{ textAlign: "left" }}>
                {activeTab === 0 && !showResetFields && (
                  <Button
                    variant="text"
                    style={{
                      color: "hsl(var(--foreground))",
                      textTransform: "none",
                    }}
                    onClick={handleForgotPassword}
                  >
                    Forgot Password?
                  </Button>
                )}
              </div>
              {showResetFields && activeTab == 0 && (
                <>
                  <TextField
                    label="Reset Code"
                    fullWidth
                    size="medium"
                    floatingLabel={true}
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    style={{
                      marginTop: "16px",
                      borderRadius: "16px",
                      height: "56px",
                    }}
                    InputProps={{
                      style: {
                        height: "56px",
                      },
                    }}
                  />
                  <TextField
                    label="New Password"
                    type={showPassword ? "text" : "password"}
                    fullWidth
                    size="medium"
                    floatingLabel={true}
                    value={newPassword}
                    onChange={(e) => handleNewPasswordChange(e)}
                    style={{
                      marginTop: "16px",
                      borderRadius: "16px",
                      height: "56px",
                    }}
                    InputProps={{
                      style: {
                        height: "56px",
                      },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={togglePasswordVisibility}
                            onMouseDown={(e) => e.preventDefault()} // Prevent focus loss on mouse down
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  {newPassword && (
                    <div
                      className="grid grid-cols-2 gap-1"
                      style={{
                        marginTop: "8px",
                        fontSize: "0.75rem",
                      }}
                    >
                      <li
                        style={{
                          color: validations.hasLowerCase ? "green" : "red",
                        }}
                      >
                        {validations.hasLowerCase ? "✓" : "×"} Lowercase letter
                      </li>
                      <li
                        style={{
                          color: validations.hasUpperCase ? "green" : "red",
                        }}
                      >
                        {validations.hasUpperCase ? "✓" : "×"} Uppercase letter
                      </li>
                      <li
                        style={{
                          color: validations.hasNumber ? "green" : "red",
                        }}
                      >
                        {validations.hasNumber ? "✓" : "×"} Number
                      </li>
                      <li
                        style={{
                          color: validations.hasMinLength ? "green" : "red",
                        }}
                      >
                        {validations.hasMinLength ? "✓" : "×"} Min 8 chars
                      </li>
                      <li
                        style={{
                          color: validations.hasSpecialChar ? "green" : "red",
                        }}
                      >
                        {validations.hasSpecialChar ? "✓" : "×"} Special char
                      </li>
                      {!validations.hasSpecialChar && (
                        <li
                          style={{
                            color: "red",
                            gridColumn: "span 2",
                            listStyle: "none",
                            fontSize: "0.7rem",
                            marginLeft: "-1em",
                          }}
                        >
                          Accepted: !@#$%^&*(),.?&quot;:{}|&lt;&gt;
                        </li>
                      )}
                      <li
                        style={{
                          color: validations.noLeadingTrailingSpace
                            ? "green"
                            : "red",
                        }}
                      >
                        {validations.noLeadingTrailingSpace ? "✓" : "×"} No
                        leading/trailing space
                      </li>
                    </div>
                  )}

                  <Button
                    variant="contained"
                    color="secondary"
                    fullWidth
                    style={{ marginTop: "16px", marginBottom: "8px" }}
                    onClick={handleResetPassword}
                  >
                    Reset Password
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    style={{ marginTop: "8px" }}
                    onClick={() => setShowResetFields(false)}
                  >
                    Cancel Reset Password
                  </Button>
                </>
              )}

              {!showResetFields && (
                <>
                  {/* Terms and Policy Checkbox */}
                  <div style={{ marginTop: "8px", marginBottom: "8px" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={termsAccepted}
                          onChange={(e) => setTermsAccepted(e.target.checked)}
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          style={{ fontSize: "0.875rem" }}
                        >
                          I agree to the{" "}
                          <span
                            style={{
                              color: "hsl(var(--primary))",
                              cursor: "pointer",
                              textDecoration: "underline",
                            }}
                            onClick={() =>
                              window.open(
                                "https://mhome.ai/terms-of-use",
                                "_blank"
                              )
                            }
                          >
                            Terms of Use
                          </span>{" "}
                          and{" "}
                          <span
                            style={{
                              color: "hsl(var(--primary))",
                              cursor: "pointer",
                              textDecoration: "underline",
                            }}
                            onClick={() =>
                              window.open(
                                "https://mhome.ai/privacy-policy",
                                "_blank"
                              )
                            }
                          >
                            Privacy Policy
                          </span>
                        </Typography>
                      }
                    />
                  </div>

                  <div style={{ textAlign: "center" }}>
                    {activeTab == 0 && (
                      <>
                        <Button
                          variant={termsAccepted ? "contained" : "outlined"}
                          onClick={() => {
                            handleSignIn();
                          }}
                          disabled={!termsAccepted}
                          fullWidth
                          color={termsAccepted ? "primary" : undefined}
                          style={{
                            borderRadius: "50px",
                            marginTop: "16px",
                            textTransform: "none",
                            boxShadow: "none",
                            ...(termsAccepted && {
                              backgroundColor: "hsl(var(--primary))",
                              color: "white",
                            }),
                          }}
                          onMouseEnter={(e) => {
                            if (termsAccepted) {
                              e.currentTarget.style.backgroundColor =
                                "hsl(var(--primary))";
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (termsAccepted) {
                              e.currentTarget.style.backgroundColor =
                                "hsl(var(--primary))";
                            }
                          }}
                        >
                          Sign In
                        </Button>
                      </>
                    )}
                    {activeTab == 1 && (
                      <div>
                        <Button
                          variant="contained"
                          onClick={() => {
                            if (codeSent) {
                              handleResendCode();
                            } else {
                              handleSignUp();
                            }
                          }}
                          disabled={isCountdownActive || !termsAccepted}
                          fullWidth
                          color="primary"
                          style={{
                            borderRadius: "50px",
                            marginTop: "24px",
                            backgroundColor:
                              activeTab === 1
                                ? "hsl(var(--primary))"
                                : "transparent",
                            color:
                              activeTab === 1 ? "white" : "hsl(var(--primary))",
                            border: `1px solid hsl(var(--primary))`,
                          }}
                        >
                          {isCountdownActive
                            ? `Resend in ${countdown}s`
                            : "Send Verification Code"}
                        </Button>
                      </div>
                    )}
                  </div>

                  {activeTab === 0 && (
                    <div style={{ textAlign: "center" }}>
                      <Button
                        variant="outlined"
                        startIcon={
                          <img
                            src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/google/google-original.svg"
                            style={{ width: 20, height: 20 }}
                            alt="Google"
                          />
                        }
                        fullWidth
                        disabled={!termsAccepted}
                        style={{
                          marginTop: "16px",
                          borderRadius: "50px",
                          textTransform: "none",
                        }}
                        onClick={handleGoogleSignIn}
                      >
                        Sign in with Google
                      </Button>
                    </div>
                  )}
                  {activeTab === 0 && (
                    <div style={{ textAlign: "center" }}>
                      <Typography variant="body2" style={{ marginTop: "16px" }}>
                        Need an account?&nbsp;{" "}
                        <span
                          style={{
                            color: "hsl(var(--foreground))",
                            cursor: "pointer",
                          }}
                          onClick={() => handleTabSwitch(1)}
                        >
                          Sign Up
                        </span>
                      </Typography>
                    </div>
                  )}
                </>
              )}

              {activeTab === 1 && (
                <div style={{ textAlign: "center" }}>
                  {showConfirm && (
                    <>
                      <div
                        className="flex items-end"
                        style={{
                          justifyContent: "flex-start",
                        }}
                      >
                        <Button
                          variant="outlined"
                          style={{ marginRight: "8px" }}
                        >
                          Verification Code :
                        </Button>
                        <TextField
                          type="text"
                          size="medium"
                          floatingLabel={true}
                          value={verificationCode}
                          onChange={(e) => setVerificationCode(e.target.value)}
                          style={{
                            flex: 1,
                            marginTop: "20px",
                            height: "56px",
                          }}
                          InputProps={{
                            style: {
                              height: "56px",
                            },
                          }}
                        />
                      </div>
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={!termsAccepted}
                        style={{
                          borderRadius: "50px",
                          marginTop: "16px",
                          textTransform: "none",
                          color: "white",
                        }}
                        onClick={handleConfirmSignUp}
                      >
                        Confirm Sign Up
                      </Button>
                    </>
                  )}
                  <Typography variant="body2" style={{ marginTop: "16px" }}>
                    Already have an account? &nbsp;{" "}
                    <span
                      style={{
                        color: "hsl(var(--foreground))",
                        cursor: "pointer",
                      }}
                      onClick={() => handleTabSwitch(0)}
                    >
                      Sign In
                    </span>
                  </Typography>
                </div>
              )}
            </>
          )}

          {/* Add this after your login form components */}
          <div
            style={{
              marginTop: "24px",
              textAlign: "center",
              fontSize: "0.875rem",
              color: "hsl(var(--muted-foreground))",
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              justifyContent="center"
              alignItems="center"
            >
              <Typography
                variant="body2"
                onClick={() => window.open("/terms-of-use", "_blank")}
                style={{
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.textDecoration = "underline";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.textDecoration = "none";
                }}
              >
                Terms of Use
              </Typography>
              <span>•</span>
              <Typography
                variant="body2"
                onClick={() => window.open("/privacy-policy", "_blank")}
                style={{
                  cursor: "pointer",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.textDecoration = "underline";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.textDecoration = "none";
                }}
              >
                Privacy Policy
              </Typography>
            </Stack>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default UserLogin;
